import { useLocation, useNavigate, useSearchParams } from '@remix-run/react'
import { useMemo } from 'react'
import {
  aCheckedListings,
  aHiddenListings,
  aSelectedListing,
  aUser,
  useAtom,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import { ListingCardBase } from '~/components'
import {
  MapView,
  useCurrentMapView,
  useEmbed,
  useMapListingLink,
} from '~/hooks'
import { buildReportListingLink, type MapListingProjection } from '~/models'

type MapListingCardProps = {
  className?: string
  listing: MapListingProjection
  onMouseOver?: () => void
  onMouseOut?: () => void
  showOriginalAttachments?: boolean
  showOriginalBrokers?: boolean
}

function shouldOpenInNewTab(
  isMetaKey: boolean,
  currentMapView: MapView,
  isEmbed: boolean,
  isReportPage: boolean,
  pathname: string
) {
  if (isMetaKey) return true
  if (isReportPage && !pathname.includes('/map')) return true
  if (currentMapView === MapView.Cards && !isEmbed) return true

  return false
}

export default function MapListingCard(props: MapListingCardProps) {
  const {
    className,
    listing,
    onMouseOver,
    onMouseOut,
    showOriginalAttachments = false,
    showOriginalBrokers = false,
  } = props
  const location = useLocation()
  const navigate = useNavigate()
  const user = useAtomValue(aUser)
  const [selectedListing, setSelectedListing] = useAtom(aSelectedListing)
  const currentMapView = useCurrentMapView()
  const [checkedListings, setCheckedListings] = useAtom(aCheckedListings)
  const setHiddenListings = useSetAtom(aHiddenListings)
  const { isEmbed, openLinksInNewTab } = useEmbed()
  const [searchParams] = useSearchParams()

  const isReportPage = useMemo(
    () => location.pathname.includes('/report'),
    [location]
  )
  const listingLink = useMapListingLink(listing)

  const link = useMemo(() => {
    const params = searchParams.size > 0 ? `?${searchParams.toString()}` : ''
    if (isReportPage) {
      return buildReportListingLink(`${listingLink}${params}`, {
        showOriginalAttachments,
        showOriginalBrokers,
      })
    } else if (isEmbed && !openLinksInNewTab) {
      if (currentMapView === MapView.Cards) {
        return `/${listing.slug}/cards${params}`
      } else {
        return `/${listing.slug}/dialog${params}`
      }
    }

    return `${listingLink}${params}`
  }, [
    listingLink,
    showOriginalAttachments,
    showOriginalBrokers,
    isReportPage,
    searchParams,
    currentMapView,
    listing.slug,
  ])

  return (
    <ListingCardBase
      className={className}
      listing={listing}
      link={link}
      lazyLoadImage={!isReportPage}
      showCompany={!isReportPage || showOriginalBrokers}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onHide={
        isReportPage
          ? undefined
          : () => {
              setHiddenListings((prev) => {
                return prev.concat(listing._id)
              })

              if (selectedListing && selectedListing._id === listing._id) {
                navigate('/')
              }
            }
      }
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()

        const isMetaKey = e.metaKey || e.ctrlKey

        if (
          shouldOpenInNewTab(
            isMetaKey,
            currentMapView,
            isEmbed,
            isReportPage,
            location.pathname
          )
        ) {
          window.open(link, '_blank', 'noreferrer')
          return
        }

        setSelectedListing(listing)
        if (!isReportPage) {
          if (
            !isEmbed ||
            (currentMapView !== MapView.Cards && openLinksInNewTab)
          ) {
            navigate(`/${listing.slug}?${searchParams.toString()}`)
          } else {
            navigate(
              `/${listing.slug}/${currentMapView === MapView.Cards ? 'cards' : 'dialog'}`
            )
          }
        }
      }}
      checked={checkedListings.includes(listing._id)}
      onCheckChanged={
        !user || isReportPage
          ? undefined
          : () => {
              if (checkedListings.includes(listing._id)) {
                setCheckedListings(
                  checkedListings.filter((id) => id !== listing._id)
                )
              } else {
                setCheckedListings([...checkedListings, listing._id])
              }
            }
      }
    />
  )
}
